import { Download, FileText, Loader2, Pencil, StickyNote } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BrandContextEntry } from "./entry";
import { formatBytes, formatEntryDate } from "./format";
import { TruncatedText } from "@/components/ui/truncated-text";

interface EntryViewDialogProps {
  entry: BrandContextEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: () => void;
  onDownload?: () => void;
  isDownloading?: boolean;
  canEdit: boolean;
}

// AUT-194: read-only View modal opened by clicking a Brand Context card. Edit is
// reached only via the "Edit" button here (which opens the existing edit dialog),
// not from the card click itself.
export function EntryViewDialog({
  entry,
  open,
  onOpenChange,
  onEdit,
  onDownload,
  isDownloading = false,
  canEdit,
}: EntryViewDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        {entry && (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div
                    className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
                      entry.type === "document" ? "bg-primary-tint-soft" : "bg-status-info-soft"
                    }`}
                  >
                    {entry.type === "document" ? (
                      <FileText className="h-4 w-4 text-primary" />
                    ) : (
                      <StickyNote className="h-4 w-4 text-foreground" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <DialogTitle className="text-left">{entry.title}</DialogTitle>
                    <DialogDescription className="text-left">
                      {entry.category} · {formatEntryDate(entry.createdAt)}
                      {entry.authorName ? ` · ${entry.authorName}` : ""}
                    </DialogDescription>
                  </div>
                </div>
                {canEdit && (
                  <Button variant="ghost" size="sm" className="gap-1.5 shrink-0" onClick={onEdit}>
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                )}
              </div>
            </DialogHeader>

            {entry.type === "learning" ? (
              <div className="space-y-4">
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{entry.body}</p>
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-surface-tint-soft px-3 py-2 flex items-center justify-between gap-2">
                <TruncatedText className="text-sm text-muted-foreground">
                  {entry.fileName} · {formatBytes(entry.fileSize)}
                </TruncatedText>
                {onDownload && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={onDownload}
                    disabled={isDownloading}
                    aria-label={`Download ${entry.title}`}
                  >
                    {isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  </Button>
                )}
              </div>
            )}

            {entry.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {entry.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="bg-muted">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
